import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import type { Customer } from '../services/customerService';

// Interface for group member
interface GroupMember {
  id: string;
  displayName: string;
  mail?: string;
  userPrincipalName: string;
}

// Interface for tenant user
interface TenantUser {
  id: string;
  displayName: string;
  mail?: string;
  userPrincipalName: string;
  jobTitle?: string;
  department?: string;
}

// Interface for track
interface Track {
  trackId: string;
  sku: string;
  name: string;
}

// Interface for group
interface Group {
  id: string;
  displayName: string;
  description?: string;
}

// Interface for group with members
interface GroupWithMembers {
  group: Group;
  track: Track;
  members: GroupMember[];
  memberCount: number;
}

// Interface for customer tenant response
interface CustomerTenantResponse {
  message: string;
  customer: Customer;
  groups: GroupWithMembers[];
  count: number;
}

// Interface for group members response
interface GroupMembersResponse {
  message: string;
  customer: Customer;
  track: Track;
  members: GroupMember[];
  count: number;
}

/**
 * Page for managing tenant groups
 */
const TenantGroupsPage = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [groups, setGroups] = useState<GroupWithMembers[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedGroupMembers, setSelectedGroupMembers] = useState<GroupMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [addingMembers, setAddingMembers] = useState(false);
  const [removingMember, setRemovingMember] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Fetch customer tenant groups on mount
  useEffect(() => {
    const fetchCustomerTenantGroups = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get<CustomerTenantResponse>(`/customer-tenant/${customerId}`);
        
        setCustomer(response.data.customer);
        setGroups(response.data.groups);
        
        // Select the first group by default if available
        if (response.data.groups.length > 0) {
          setSelectedGroup(response.data.groups[0].track.trackId);
          setSelectedGroupMembers(response.data.groups[0].members);
        }
      } catch (err) {
        console.error('Error fetching customer tenant groups:', err);
        setError('Failed to load customer tenant groups. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (customerId) {
      fetchCustomerTenantGroups();
    }
  }, [customerId]);

  // Fetch group members when selected group changes
  useEffect(() => {
    const fetchGroupMembers = async () => {
      if (!selectedGroup) return;
      
      try {
        setLoadingMembers(true);
        
        const response = await api.get<GroupMembersResponse>(
          `/customer-tenant/${customerId}/group/${selectedGroup}/members`
        );
        
        setSelectedGroupMembers(response.data.members);
      } catch (err) {
        console.error('Error fetching group members:', err);
        // Don't set error state here to avoid disrupting the UI
      } finally {
        setLoadingMembers(false);
      }
    };
    
    if (selectedGroup) {
      fetchGroupMembers();
    }
  }, [customerId, selectedGroup]);

  // Fetch all tenant users
  useEffect(() => {
    const fetchTenantUsers = async () => {
      if (!customerId) return;
      
      try {
        setLoadingUsers(true);
        
        const response = await api.get<{
          message: string;
          customer: Customer;
          users: TenantUser[];
          count: number;
        }>(`/customer-tenant/${customerId}/users`);
        
        setTenantUsers(response.data.users);
      } catch (err) {
        console.error('Error fetching tenant users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };
    
    if (customerId) {
      fetchTenantUsers();
    }
  }, [customerId]);

  // Handle group selection
  const handleGroupSelect = (trackId: string) => {
    setSelectedGroup(trackId);
    
    // Find the group in the groups array
    const group = groups.find(g => g.track.trackId === trackId);
    if (group) {
      setSelectedGroupMembers(group.members);
    }
  };

  // Handle back button click
  const handleBack = () => {
    navigate('/customers');
  };
  
  // Handle add member button click
  const handleAddMemberClick = () => {
    setShowAddMemberModal(true);
    setSearchTerm('');
    setSelectedUsers([]);
  };
  
  // Handle add member modal close
  const handleCloseAddMemberModal = () => {
    setShowAddMemberModal(false);
    setSelectedUsers([]);
  };
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  // Filter tenant users based on search term
  const filteredUsers = tenantUsers.filter(user => {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    return (
      user.displayName.toLowerCase().includes(term) ||
      (user.mail && user.mail.toLowerCase().includes(term)) ||
      user.userPrincipalName.toLowerCase().includes(term)
    );
  });
  
  // Check if user is already a member of the selected group
  const isUserInGroup = (userId: string) => {
    return selectedGroupMembers.some(member => member.id === userId);
  };
  
  // Handle user selection toggle
  const handleUserSelectionToggle = (userId: string) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };
  
  // Handle select all users
  const handleSelectAllUsers = () => {
    const availableUsers = filteredUsers
      .filter(user => !isUserInGroup(user.id))
      .map(user => user.id);
    
    if (selectedUsers.length === availableUsers.length) {
      // If all are selected, deselect all
      setSelectedUsers([]);
    } else {
      // Otherwise, select all available users
      setSelectedUsers(availableUsers);
    }
  };
  
  // Handle add selected members to group
  const handleAddSelectedMembers = async () => {
    if (!customerId || !selectedGroup || selectedUsers.length === 0) return;
    
    try {
      setAddingMembers(true);
      
      // Add each selected user to the group
      for (const userId of selectedUsers) {
        await api.post(`/customer-tenant/${customerId}/group/${selectedGroup}/members/${userId}`);
      }
      
      // Refresh group members
      const response = await api.get<GroupMembersResponse>(
        `/customer-tenant/${customerId}/group/${selectedGroup}/members`
      );
      
      setSelectedGroupMembers(response.data.members);
      
      // Also refresh the groups to update the member count
      const groupsResponse = await api.get<CustomerTenantResponse>(`/customer-tenant/${customerId}`);
      setGroups(groupsResponse.data.groups);
      
      // Show success message
      alert(`${selectedUsers.length} users added to group successfully`);
      
      // Close modal and clear selection
      setShowAddMemberModal(false);
      setSelectedUsers([]);
    } catch (err) {
      console.error('Error adding members to group:', err);
      alert('Failed to add members to group. Please try again.');
    } finally {
      setAddingMembers(false);
    }
  };
  
  // Handle remove member from group
  const handleRemoveMember = async (userId: string) => {
    if (!customerId || !selectedGroup) return;
    
    // Ask for confirmation
    if (!window.confirm('Are you sure you want to remove this user from the group?')) {
      return;
    }
    
    try {
      setRemovingMember(userId);
      
      await api.delete(`/customer-tenant/${customerId}/group/${selectedGroup}/members/${userId}`);
      
      // Refresh group members
      const response = await api.get<GroupMembersResponse>(
        `/customer-tenant/${customerId}/group/${selectedGroup}/members`
      );
      
      setSelectedGroupMembers(response.data.members);
      
      // Also refresh the groups to update the member count
      const groupsResponse = await api.get<CustomerTenantResponse>(`/customer-tenant/${customerId}`);
      setGroups(groupsResponse.data.groups);
      
      // Show success message
      alert('User removed from group successfully');
    } catch (err) {
      console.error('Error removing member from group:', err);
      alert('Failed to remove member from group. Please try again.');
    } finally {
      setRemovingMember(null);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-lg">
        <h1 className="text-2xl font-bold mb-lg">Loading tenant groups...</h1>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-lg">
        <h1 className="text-2xl font-bold mb-lg">Error</h1>
        <div className="bg-error p-md rounded-md text-white mb-lg">
          {error}
        </div>
        <button className="btn btn-primary" onClick={handleBack}>
          Back to Customers
        </button>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="container mx-auto p-lg">
        <h1 className="text-2xl font-bold mb-lg">Customer Not Found</h1>
        <button className="btn btn-primary" onClick={handleBack}>
          Back to Customers
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-lg">
      <div className="flex justify-between items-center mb-lg">
        <h1 className="text-2xl font-bold">Tenant Groups for {customer.customerName}</h1>
        <div className="flex gap-sm">
          <button 
            className="btn btn-primary"
            onClick={async () => {
              if (window.confirm('Are you sure you want to sync users from Entra ID? This may take a few minutes.')) {
                try {
                  setLoading(true);
                  await api.post('/sync/entra-users', {
                    customerId: customerId,
                    async: true
                  });
                  alert('Entra ID User Sync has been triggered. This process will run in the background.');
                } catch (err) {
                  console.error('Error triggering Entra ID User Sync:', err);
                  alert('Failed to trigger Entra ID User Sync. Please try again.');
                } finally {
                  setLoading(false);
                }
              }
            }}
            disabled={loading}
          >
            Sync from Entra ID
          </button>
          <button className="btn btn-secondary" onClick={handleBack}>
            Back to Customers
          </button>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="bg-info p-md rounded-md text-white mb-lg">
          No groups found for this customer. Make sure the tenant has been prepared.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
          {/* Groups list */}
          <div className="col-span-1 bg-background-paper p-md rounded-md shadow-sm">
            <h2 className="text-xl font-bold mb-md">Groups</h2>
            <div className="flex flex-col gap-sm">
              {groups.map((group) => (
                <button
                  key={group.track.trackId}
                  className={`p-sm rounded-md text-left ${
                    selectedGroup === group.track.trackId
                      ? 'bg-primary text-white'
                      : 'bg-background-light hover:bg-background-hover'
                  }`}
                  onClick={() => handleGroupSelect(group.track.trackId)}
                >
                  <div className="font-bold">{group.track.name}</div>
                  <div className="text-sm opacity-80">{group.group.displayName}</div>
                  <div className="text-sm mt-xs">
                    {group.memberCount} {group.memberCount === 1 ? 'member' : 'members'}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Group members */}
          <div className="col-span-1 md:col-span-2 bg-background-paper p-md rounded-md shadow-sm">
            {selectedGroup ? (
              <>
                <div className="flex justify-between items-center mb-md">
                  <h2 className="text-xl font-bold">
                    {groups.find(g => g.track.trackId === selectedGroup)?.track.name} Members
                  </h2>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={handleAddMemberClick}
                  >
                    Add Member
                  </button>
                </div>
                
                {loadingMembers ? (
                  <div className="p-md text-center">Loading members...</div>
                ) : selectedGroupMembers.length === 0 ? (
                  <div className="bg-info p-md rounded-md text-white">
                    No members in this group. Click "Add Member" to add users to this group.
                  </div>
                ) : (
                  <>
                    <div className="mb-md">
                      <h3 className="text-lg">Members ({selectedGroupMembers.length})</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-divider">
                            <th className="p-sm text-left">Name</th>
                            <th className="p-sm text-left">Email</th>
                            <th className="p-sm text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedGroupMembers.map((member) => (
                            <tr key={member.id} className="border-b border-divider">
                              <td className="p-sm">{member.displayName}</td>
                              <td className="p-sm">{member.mail || member.userPrincipalName}</td>
                              <td className="p-sm">
                                <button
                                  className="btn btn-text btn-sm text-error"
                                  onClick={() => handleRemoveMember(member.id)}
                                  disabled={removingMember === member.id}
                                >
                                  {removingMember === member.id ? 'Removing...' : 'Remove'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="p-md text-center">
                Select a group to view its members
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background-paper p-lg rounded-md shadow-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-lg">
              <h2 className="text-xl font-bold">Add Members to Group</h2>
              <button 
                className="btn btn-text"
                onClick={handleCloseAddMemberModal}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="mb-md">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  className="p-sm w-full pl-10"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  autoFocus
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </div>
                {searchTerm && (
                  <button
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    onClick={() => setSearchTerm('')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex justify-between items-center mt-2">
                <div className="text-sm text-gray-500">
                  Found {filteredUsers.length} of {tenantUsers.length} users
                </div>
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={selectedUsers.length > 0 && 
                        selectedUsers.length === filteredUsers.filter(user => !isUserInGroup(user.id)).length}
                      onChange={handleSelectAllUsers}
                    />
                    Select All
                  </label>
                </div>
              </div>
            </div>
            
            <div className="overflow-y-auto flex-grow">
              {loadingUsers ? (
                <div className="p-md text-center">Loading users...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-md text-center">No users found</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-divider">
                      <th className="p-sm text-left">Select</th>
                      <th className="p-sm text-left">Name</th>
                      <th className="p-sm text-left">Email</th>
                      <th className="p-sm text-left">Job Title</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b border-divider hover:bg-background-hover">
                        <td className="p-sm">
                          {isUserInGroup(user.id) ? (
                            <span className="text-success">Already in group</span>
                          ) : (
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => handleUserSelectionToggle(user.id)}
                            />
                          )}
                        </td>
                        <td className="p-sm">{user.displayName}</td>
                        <td className="p-sm">{user.mail || user.userPrincipalName}</td>
                        <td className="p-sm">{user.jobTitle || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            <div className="mt-lg flex justify-between">
              <div>
                {selectedUsers.length > 0 && (
                  <span className="text-sm">{selectedUsers.length} users selected</span>
                )}
              </div>
              <div className="flex gap-sm">
                <button 
                  className="btn btn-secondary"
                  onClick={handleCloseAddMemberModal}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleAddSelectedMembers}
                  disabled={addingMembers || selectedUsers.length === 0}
                >
                  {addingMembers ? (
                    <>
                      <div className="spinner-sm mr-xs"></div>
                      Adding...
                    </>
                  ) : `Add ${selectedUsers.length} Users`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantGroupsPage;
